<template>
  <v-container fluid class="pa-0 pa-md-4 fill-height align-start d-flex flex-column">
    <v-card v-if="canViewAll && !loading && $vuetify.breakpoint.mdAndUp" class="mb-4 w-100 rounded-lg" color="#1E1E1E" dark elevation="0">
      <div class="pa-4 d-flex align-center overflow-x-auto hide-scrollbar">
        <div class="mr-6 flex-shrink-0">
          <div class="caption grey--text text-uppercase font-weight-bold">Total</div>
          <div class="text-h4 font-weight-bold primary--text">CHF {{ grandTotal.toFixed(2) }}</div>
        </div>
        <v-divider vertical class="mx-4"></v-divider>
        <div class="d-flex">
          <v-chip v-for="(amount, name) in statsByStudio" :key="name" class="mr-2" color="grey darken-3" label>
            <span class="grey--text mr-2">{{ name }}:</span>
            <span class="white--text font-weight-bold">{{ amount.toFixed(2) }}</span>
          </v-chip>
        </div>
      </div>
    </v-card>

    <v-card flat width="100%" class="mb-4 rounded-lg" color="#1E1E1E" dark>
      <template>
        <v-card flat width="100%" class="mb-4 rounded-lg" color="#1E1E1E" dark>
          <div class="d-flex align-center pa-3">
            <v-btn v-if="$vuetify.breakpoint.smAndDown && canViewAll" icon @click="statsDialog = true" color="primary" class="mr-2">
              <v-icon>mdi-chart-pie</v-icon>
            </v-btn>

            <v-btn-toggle v-if="$vuetify.breakpoint.mdAndUp" v-model="viewMode" mandatory rounded class="mr-4" background-color="#333" borderless>
              <v-btn value="grid" small> <v-icon small>mdi-view-grid</v-icon> </v-btn>
              <v-btn value="table" small> <v-icon small>mdi-table</v-icon> </v-btn>
            </v-btn-toggle>

            <v-text-field
              v-model="filters.search"
              placeholder="Suche (Shop, Betrag, Item)..."
              prepend-inner-icon="mdi-magnify"
              outlined
              dense
              hide-details
              rounded
              class="flex-grow-1"
              style="max-width: 400px"
              color="primary"
              @keydown.enter="loadData"></v-text-field>

            <v-btn icon @click="showFilters = !showFilters" class="d-md-none mr-2" :color="showFilters ? 'primary' : ''">
              <v-icon>{{ showFilters ? "mdi-chevron-up" : "mdi-filter-variant" }}</v-icon>
            </v-btn>

            <div v-if="$vuetify.breakpoint.mdAndUp" class="d-flex align-center ml-2">
              <v-select
                v-model="filters.studioIds"
                :items="filterStudios"
                item-text="name"
                item-value="_id"
                placeholder="Alle Studios"
                outlined
                dense
                hide-details
                rounded
                multiple
                small-chips
                deletable-chips
                style="width: 200px"
                class="mx-2"
                @change="loadData"></v-select>

              <v-menu ref="menu" v-model="dateMenu" :close-on-content-click="false" offset-y min-width="auto" dark>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn v-bind="attrs" v-on="on" text rounded class="grey--text text--lighten-1">
                    <v-icon left small>mdi-calendar</v-icon> {{ dateDisplay }}
                  </v-btn>
                </template>
                <v-card color="#333">
                  <v-tabs v-model="dateMode" dark background-color="transparent" grow height="36">
                    <v-tab :key="0" class="caption">Monat</v-tab>
                    <v-tab v-if="false" :key="1" class="caption">Bereich</v-tab>
                  </v-tabs>
                  <v-date-picker
                    v-if="dateMode === 0"
                    v-model="filters.month"
                    type="month"
                    no-title
                    scrollable
                    locale="de-DE"
                    @input="
                      dateMenu = false
                      loadData()
                    "
                    dark></v-date-picker>
                  <v-date-picker v-else v-model="filters.range" range no-title scrollable locale="de-DE" @input="onRangeInput" dark></v-date-picker>
                </v-card>
              </v-menu>

              <v-btn v-if="canViewAll" icon color="grey" @click="downloadExport" :loading="loadingExport">
                <v-icon>mdi-file-pdf-box</v-icon>
              </v-btn>

              <v-btn color="primary" rounded elevation="0" @click="openUploadDialog" class="ml-2 px-6"> <v-icon left>mdi-plus</v-icon> Upload </v-btn>
            </div>
          </div>

          <v-expand-transition>
            <div v-if="showFilters" class="d-md-none px-3 pb-3">
              <v-divider class="mb-3 grey darken-3"></v-divider>

              <v-row dense>
                <v-col v-if="$vuetify.breakpoint.mdAndUp" cols="12" class="d-flex justify-center mb-2">
                  <v-btn-toggle v-model="viewMode" mandatory rounded background-color="#333" borderless>
                    <v-btn value="grid" small> <v-icon small class="mr-1">mdi-view-grid</v-icon> Grid </v-btn>
                    <v-btn value="table" small> <v-icon small class="mr-1">mdi-table</v-icon> Liste </v-btn>
                  </v-btn-toggle>
                </v-col>

                <v-col cols="12">
                  <v-select
                    v-model="filters.studioIds"
                    :items="filterStudios"
                    item-text="name"
                    item-value="_id"
                    label="Studios filtern"
                    outlined
                    dense
                    hide-details
                    rounded
                    multiple
                    small-chips
                    deletable-chips
                    @change="loadData"></v-select>
                </v-col>

                <v-col cols="12">
                  <v-menu
                    ref="mobileMenu"
                    v-model="mobileDateMenu"
                    :close-on-content-click="false"
                    offset-y
                    min-width="auto"
                    dark
                    left
                    content-class="elevation-10">
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn block outlined rounded class="justify-start grey--text text--lighten-1" v-bind="attrs" v-on="on">
                        <v-icon left>mdi-calendar</v-icon> {{ dateDisplay }}
                      </v-btn>
                    </template>
                    <v-card color="#333">
                      <v-tabs v-model="dateMode" dark background-color="transparent" grow height="36">
                        <v-tab :key="0" class="caption">Monat</v-tab>
                        <v-tab :key="1" class="caption">Bereich</v-tab>
                      </v-tabs>
                      <v-date-picker
                        v-if="dateMode === 0"
                        v-model="filters.month"
                        type="month"
                        no-title
                        scrollable
                        locale="de-DE"
                        @input="
                          mobileDateMenu = false
                          loadData()
                        "
                        dark></v-date-picker>
                      <v-date-picker
                        v-else
                        v-model="filters.range"
                        range
                        no-title
                        scrollable
                        locale="de-DE"
                        @input="onRangeInput"
                        dark></v-date-picker>
                    </v-card>
                  </v-menu>
                </v-col>

                <v-col cols="6">
                  <v-btn block outlined rounded color="grey" @click="downloadExport" :loading="loadingExport" :disabled="!canViewAll" height="40">
                    <v-icon left>mdi-file-pdf-box</v-icon> Export
                  </v-btn>
                </v-col>

                <v-col cols="6">
                  <v-btn block color="primary" rounded elevation="0" @click="openUploadDialog" height="40">
                    <v-icon left>mdi-plus</v-icon> Upload
                  </v-btn>
                </v-col>
              </v-row>
            </div>
          </v-expand-transition>
        </v-card>
      </template>
    </v-card>

    <div v-if="loading" class="d-flex flex-column align-center justify-center mt-12 w-100">
      <v-progress-circular indeterminate color="primary" size="64" width="4"></v-progress-circular>
      <div class="mt-4 grey--text">Lade Belege...</div>
    </div>

    <div v-else class="w-100">
      <div v-if="viewMode === 'grid'">
        <div class="d-md-none">
          <v-card
            v-for="bill in bills"
            :key="bill._id"
            class="d-flex align-center pa-3 mb-2 rounded-lg elevation-2 mobile-row"
            color="#1E1E1E"
            @click="openEditDialog(bill)"
            v-ripple>
            <v-avatar tile size="56" class="rounded mr-3 grey darken-3">
              <v-img v-if="bill.previewUrl" :src="bill.previewUrl" aspect-ratio="1">
                <template v-slot:placeholder>
                  <v-row class="fill-height ma-0" align="center" justify="center">
                    <v-progress-circular indeterminate color="grey" size="20"></v-progress-circular>
                  </v-row>
                </template>
              </v-img>
              <v-icon v-else color="grey">mdi-file-image</v-icon>
            </v-avatar>

            <div class="flex-grow-1 overflow-hidden mr-2">
              <div class="white--text font-weight-bold text-truncate text-body-1">{{ bill.storeName || "Unbekannt" }}</div>
              <div class="caption grey--text d-flex align-center mt-1">
                <v-icon x-small color="grey" class="mr-1">mdi-calendar</v-icon>
                {{ formatDate(bill.date) }}
                <span class="mx-1">•</span>
                {{ bill.uploader.vorname }}
              </div>
            </div>

            <div class="d-flex flex-column align-end justify-center">
              <div class="text-h6 font-weight-bold success--text text--lighten-1">
                {{ bill.amount ? parseFloat(bill.amount).toFixed(2) : "0.00" }}
              </div>
              <div class="d-flex align-center mt-1">
                <v-btn icon x-small color="red darken-2" @click.stop="confirmDelete(bill)">
                  <v-icon small>mdi-delete</v-icon>
                </v-btn>
                <div class="caption grey--text text--darken-1 text-truncate" style="max-width: 80px">
                  {{ bill.studio ? bill.studio.name : "N/A" }}
                </div>
              </div>
            </div>
          </v-card>

          <div v-if="bills.length === 0" class="text-center grey--text mt-10">
            <v-icon size="64" color="grey darken-3">mdi-receipt-text-off</v-icon>
            <div class="mt-4">Keine Belege gefunden</div>
          </div>
        </div>

        <v-row class="ma-0 hidden-sm-and-down">
          <v-col cols="12" sm="6" md="4" lg="3" xl="2" v-for="bill in bills" :key="bill._id" class="pa-2">
            <v-card dark color="#1E1E1E" class="rounded-lg fill-height d-flex flex-column hover-card elevation-4 border-card">
              <v-img
                :src="bill.previewUrl || 'https://app.wellcomefit.ch/img/einmalig/imageUploadPlaceholder.jpg'"
                height="160px"
                class="grey darken-4 align-end"
                @click="openPreview(bill)"
                style="cursor: pointer; border-radius: 8px 8px 0 0">
                <template v-slot:placeholder>
                  <v-row class="fill-height ma-0" align="center" justify="center">
                    <v-progress-circular indeterminate color="grey darken-3"></v-progress-circular>
                  </v-row>
                </template>
                <div style="background: linear-gradient(to top, rgba(0, 0, 0, 0.9), transparent); width: 100%; padding: 10px">
                  <div class="d-flex justify-space-between align-end">
                    <span class="white--text font-weight-medium">{{ formatDate(bill.date) }}</span>
                    <v-icon v-if="bill.isVerified" small color="green accent-4">mdi-check-decagram</v-icon>
                  </div>
                </div>
              </v-img>

              <v-card-text class="flex-grow-1 pt-3 pb-2">
                <div class="d-flex justify-space-between align-start mb-1">
                  <v-chip x-small color="grey darken-3" class="grey--text text--lighten-1">{{ bill.studio ? bill.studio.name : "N/A" }}</v-chip>
                </div>
                <div class="d-flex justify-space-between align-start mb-1">
                  <div class="text-subtitle-1 white--text font-weight-bold text-truncate pr-2">
                    {{ bill.storeName || "Unbekannt" }}
                  </div>
                </div>
                <div class="d-flex justify-space-between align-start mb-1">
                  <div class="text-h6 font-weight-bold primary--text">
                    {{ bill.amount ? parseFloat(bill.amount).toFixed(2) : "0.00" }}
                  </div>
                </div>

                <div class="d-flex align-center justify-space-between mt-3">
                  <div class="d-flex align-center">
                    <v-avatar size="24" color="grey darken-3" class="mr-2">
                      <span class="white--text caption font-weight-bold">{{ getInitials(bill.uploader) }}</span>
                    </v-avatar>
                    <div class="caption grey--text">{{ bill.uploader.vorname }}</div>
                  </div>
                </div>
              </v-card-text>

              <v-divider class="grey darken-3"></v-divider>
              <v-card-actions class="pa-2">
                <v-btn icon small color="grey lighten-1" @click="openPreview(bill)"><v-icon>mdi-eye-outline</v-icon></v-btn>
                <v-btn icon small color="blue lighten-1" @click="openEditDialog(bill)"><v-icon>mdi-pencil</v-icon></v-btn>

                <v-spacer></v-spacer>
                <v-btn icon small color="red darken-2" @click="confirmDelete(bill)"><v-icon>mdi-delete</v-icon></v-btn>
                <v-btn icon small color="primary" @click="downloadFile(bill)"><v-icon>mdi-download-outline</v-icon></v-btn>
              </v-card-actions>
            </v-card>
          </v-col>
        </v-row>
      </div>

      <v-card v-if="viewMode === 'table'" dark color="#1E1E1E" class="rounded-lg">
        <v-data-table
          :headers="tableHeaders"
          :items="bills"
          :items-per-page="50"
          class="transparent"
          @click:row="openPreview"
          :expanded.sync="expanded"
          item-key="_id"
          show-expand
          :mobile-breakpoint="0">
          <template v-slot:item.date="{ item }">
            {{ formatDate(item.date) }}
          </template>

          <template v-slot:item.uploader="{ item }">
            <div class="d-flex align-center">
              <v-avatar size="24" color="grey darken-3" class="mr-2">
                <span class="white--text caption font-weight-bold">{{ getInitials(item.uploader) }}</span>
              </v-avatar>
              {{ item.uploader.vorname }}
            </div>
          </template>

          <template v-slot:item.amount="{ item }">
            <span class="font-weight-bold success--text">CHF {{ item.amount.toFixed(2) }}</span>
          </template>

          <template v-slot:item.studio="{ item }">
            <v-chip x-small color="grey darken-3">{{ item.studio ? item.studio.name : "N/A" }}</v-chip>
          </template>

          <template v-slot:item.actions="{ item }">
            <v-btn icon small color="primary" @click.stop="openEditDialog(item)" class="mr-2">
              <v-icon small>mdi-pencil</v-icon>
            </v-btn>
            <v-btn icon small color="red darken-2" @click.stop="confirmDelete(item)">
              <v-icon small>mdi-delete</v-icon>
            </v-btn>
            <v-btn icon small color="grey" @click.stop="downloadFile(item)">
              <v-icon small>mdi-download</v-icon>
            </v-btn>
          </template>
          <template v-slot:expanded-item="{ headers, item }">
            <td :colspan="headers.length" class="pa-0">
              <v-card flat dark color="#252525" class="pa-4 rounded-0">
                <div class="subtitle-2 grey--text mb-2">
                  <v-icon small left color="grey">mdi-receipt-text-outline</v-icon>
                  Positionen ({{ item.lineItems ? item.lineItems.length : 0 }})
                </div>

                <div v-if="item.lineItems && item.lineItems.length > 0">
                  <v-row v-for="(line, i) in item.lineItems" :key="i" dense class="caption grey--text text--lighten-1 py-1 border-bottom-dashed">
                    <v-col cols="8" class="text-truncate">
                      <span class="font-weight-bold mr-2">{{ line.quantity || 1 }}x</span>

                      <span
                        :class="{
                          'primary--text font-weight-bold': filters.search && line.name.toLowerCase().includes(filters.search.toLowerCase()),
                        }">
                        {{ line.name }}
                      </span>
                    </v-col>
                    <v-col cols="4" class="text-right">
                      {{ line.price ? parseFloat(line.price).toFixed(2) : "0.00" }}
                    </v-col>
                  </v-row>

                  <v-divider class="my-2 grey darken-2"></v-divider>
                  <div class="d-flex justify-end caption font-weight-bold">
                    Summe Positionen: {{ item.lineItems.reduce((a, b) => a + (b.price || 0), 0).toFixed(2) }}
                  </div>
                </div>

                <div v-else class="caption grey--text font-italic pl-6">Keine einzelnen Positionen erfasst.</div>
              </v-card>
            </td>
          </template>
        </v-data-table>
      </v-card>
    </div>

    <v-btn fab fixed bottom right large color="primary" elevation="8" class="d-md-none mb-4 mr-2" @click="openUploadDialog" style="z-index: 90">
      <v-icon>mdi-camera-plus</v-icon>
    </v-btn>

    <v-dialog v-model="statsDialog" max-width="400" dark content-class="rounded-lg">
      <v-card color="#1E1E1E">
        <v-card-title>Statistik</v-card-title>
        <v-card-text>
          <div class="text-center mb-4 mt-2">
            <div class="caption grey--text text-uppercase">Gesamt Total</div>
            <div class="text-h3 font-weight-bold primary--text">CHF {{ grandTotal.toFixed(2) }}</div>
          </div>
          <v-divider class="mb-4 grey darken-3"></v-divider>
          <div v-for="(amount, name) in statsByStudio" :key="name" class="d-flex justify-space-between mb-3 align-center">
            <span class="grey--text">{{ name }}</span>
            <v-chip color="grey darken-3" label small class="font-weight-bold white--text"> CHF {{ amount.toFixed(2) }} </v-chip>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text color="primary" @click="statsDialog = false">Schließen</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="deleteDialog" max-width="450" dark>
      <v-card color="#1E1E1E" class="rounded-lg">
        <v-card-title class="headline text-h5 font-weight-bold">
          <v-icon color="red" left large>mdi-alert-circle-outline</v-icon> Beleg löschen?
        </v-card-title>
        <v-card-text class="pt-2 text-body-1 grey--text text--lighten-1">
          Möchtest du den Beleg von <b>{{ billToDelete?.storeName }}</b> ({{ billToDelete ? parseFloat(billToDelete.amount).toFixed(2) : "" }} CHF)
          wirklich unwiderruflich löschen?
        </v-card-text>
        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn text class="grey--text" @click="deleteDialog = false">Abbrechen</v-btn>
          <v-btn color="red darken-1" class="px-4" rounded elevation="0" @click="executeDelete" :loading="deleting"> Löschen </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="dialog" :fullscreen="$vuetify.breakpoint.xsOnly" max-width="950" persistent dark transition="dialog-bottom-transition">
      <v-card class="d-flex flex-column">
        <v-toolbar color="#1E1E1E" elevation="0">
          <v-btn icon @click="dialog = false"><v-icon>mdi-close</v-icon></v-btn>
          <v-toolbar-title class="font-weight-bold">
            {{ isEditMode ? "Beleg Bearbeiten" : aiStep === 1 ? "Neuer Beleg" : aiStep === 2 ? "AI Analyse" : "Prüfung" }}
          </v-toolbar-title>
          <v-spacer></v-spacer>
        </v-toolbar>

        <v-card-text class="flex-grow-1 pa-0 d-flex flex-column" style="position: relative; overflow-y: auto">
          <div v-if="aiStep === 1" class="d-flex flex-column align-center justify-center fill-height pa-6">
            <div
              class="upload-zone d-flex flex-column align-center justify-center w-100 rounded-xl dashed-border hover-scale"
              style="min-height: 300px; background: #1e1e1e; cursor: pointer"
              @click="triggerUploadFlow">
              <div class="circle-bg mb-6">
                <v-icon size="48" color="primary">mdi-camera-outline</v-icon>
              </div>
              <div class="text-h6 mb-2 text-center">
                {{ $vuetify.breakpoint.xsOnly ? "Foto machen" : "Datei auswählen" }}
              </div>

              <v-file-input
                ref="fileInput"
                v-model="uploadFile"
                :accept="$vuetify.breakpoint.xsOnly ? 'image/*' : 'image/*,application/pdf'"
                :capture="$vuetify.breakpoint.xsOnly ? 'environment' : undefined"
                class="d-none"
                @change="onFileSelected"></v-file-input>
            </div>
          </div>

          <div v-if="aiStep === 2" class="d-flex flex-column align-center justify-center fill-height pa-6">
            <div class="scanner-container mb-8">
              <div class="scanner-line"></div>
              <v-icon size="120" color="grey darken-3">mdi-receipt-text-outline</v-icon>
            </div>
            <div class="text-h5 font-weight-bold white--text mb-2 text-center">{{ loadingMessage }}</div>
            <div class="caption grey--text text--darken-1 mb-8">Dies kann bis zu 40 Sekunden dauern...</div>
            <v-progress-linear indeterminate color="primary" rounded height="6" style="max-width: 200px; opacity: 0.8"></v-progress-linear>
          </div>

          <div v-if="aiStep === 3" class="fill-height d-flex flex-column">
            <v-tabs v-if="$vuetify.breakpoint.xsOnly" v-model="mobileTab" background-color="#1E1E1E" grow dark centered icons-and-text height="60">
              <v-tab><v-icon class="mb-1">mdi-file-document-edit-outline</v-icon>Daten</v-tab>
              <v-tab><v-icon class="mb-1">mdi-image-outline</v-icon>Beleg</v-tab>
            </v-tabs>

            <div class="d-flex flex-grow-1 flex-column flex-md-row" style="overflow: hidden">
              <div
                class="grey darken-4 d-flex align-center justify-center"
                :class="{ 'd-none': $vuetify.breakpoint.xsOnly && mobileTab !== 1, 'col-md-5': true }"
                style="position: relative; overflow: hidden; height: 100%">
                <v-img v-if="currentUploadPreview" :src="currentUploadPreview" contain max-height="100%"></v-img>
                <div v-else class="caption grey--text">Keine Vorschau</div>
              </div>

              <div
                class="pa-4 pa-md-6"
                :class="{ 'd-none': $vuetify.breakpoint.xsOnly && mobileTab !== 0, 'col-md-7': true }"
                style="overflow-y: auto; height: 100%">
                <v-form ref="form" v-model="valid">
                  <v-card color="#1E1E1E" class="pa-4 rounded-lg mb-4 border-highlight">
                    <div class="caption grey--text text-uppercase mb-1 text-center">Betrag (CHF)</div>
                    <v-text-field
                      v-model="newBill.amount"
                      type="number"
                      placeholder="0.00"
                      solo
                      flat
                      background-color="transparent"
                      class="text-h3 font-weight-bold primary--text pa-0 ma-0 custom-amount-input"
                      hide-details
                      height="60"
                      :rules="[(v) => !!v || 'Betrag fehlt']"></v-text-field>
                  </v-card>

                  <v-row dense>
                    <v-col cols="12">
                      <v-text-field
                        v-model="newBill.storeName"
                        label="Händler / Geschäft"
                        outlined
                        background-color="#1E1E1E"
                        prepend-inner-icon="mdi-store"></v-text-field>
                    </v-col>
                    <v-col cols="6">
                      <v-menu v-model="dateMenuUpload" offset-y min-width="auto" dark>
                        <template v-slot:activator="{ on, attrs }">
                          <v-text-field
                            v-model="newBill.date"
                            label="Datum"
                            prepend-inner-icon="mdi-calendar"
                            readonly
                            v-bind="attrs"
                            v-on="on"
                            outlined
                            background-color="#1E1E1E"
                            :rules="[(v) => !!v || 'Pflichtfeld']"></v-text-field>
                        </template>
                        <v-date-picker v-model="newBill.date" no-title locale="de-DE" @input="dateMenuUpload = false" dark></v-date-picker>
                      </v-menu>
                    </v-col>
                    <v-col cols="6">
                      <v-text-field v-model="newBill.tax" label="MWST (CHF)" type="number" outlined background-color="#1E1E1E"></v-text-field>
                    </v-col>
                    <v-col cols="12">
                      <v-select
                        v-model="newBill.studio"
                        :items="studios"
                        item-text="name"
                        item-value="_id"
                        label="Studio Zuordnung"
                        outlined
                        background-color="#1E1E1E"
                        prepend-inner-icon="mdi-map-marker"
                        :rules="[(v) => !!v || 'Bitte wählen']"></v-select>
                    </v-col>
                  </v-row>

                  <div class="d-flex justify-space-between align-center mt-4 mb-2">
                    <span class="subtitle-2 grey--text">Positionen</span>
                    <v-btn text small color="primary" @click="addLineItem"><v-icon left>mdi-plus</v-icon>Add</v-btn>
                  </div>

                  <v-slide-y-transition group>
                    <div
                      v-for="(item, index) in newBill.lineItems"
                      :key="index"
                      class="d-flex align-center mb-2 pa-2 rounded"
                      style="background: #1e1e1e">
                      <v-text-field
                        v-model="item.name"
                        placeholder="Artikel"
                        hide-details
                        dense
                        flat
                        solo
                        background-color="transparent"
                        class="caption-input"
                        style="flex: 3"></v-text-field>
                      <v-divider vertical class="mx-2"></v-divider>
                      <v-text-field
                        v-model="item.price"
                        placeholder="0.00"
                        hide-details
                        dense
                        flat
                        solo
                        type="number"
                        background-color="transparent"
                        style="flex: 1"
                        class="caption-input text-right"></v-text-field>
                      <v-btn icon x-small color="red lighten-2" class="ml-2" @click="removeLineItem(index)"><v-icon>mdi-close</v-icon></v-btn>
                    </div>
                  </v-slide-y-transition>
                </v-form>
              </div>
            </div>
          </div>
        </v-card-text>

        <v-divider v-if="aiStep === 3"></v-divider>
        <v-card-actions v-if="aiStep === 3" class="pa-4">
          <v-btn v-if="!isEditMode" text color="grey" @click="aiStep = 1" :disabled="uploading">Neu Scannen</v-btn>
          <v-spacer></v-spacer>
          <v-btn x-large color="primary" class="px-8 font-weight-bold shadow-glow" rounded @click="saveBill" :loading="uploading" :disabled="!valid">
            <v-icon left>mdi-check</v-icon> {{ isEditMode ? "Aktualisieren" : "Speichern" }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="previewDialog" fullscreen hide-overlay transition="fade-transition" dark>
      <v-card color="black" class="d-flex flex-column justify-center align-center">
        <v-btn icon large absolute top right @click="previewDialog = false" class="mt-4 mr-4" style="z-index: 10; background: rgba(0, 0, 0, 0.5)">
          <v-icon>mdi-close</v-icon>
        </v-btn>
        <img :src="currentPreviewUrl" style="max-width: 100vw; max-height: 100vh; object-fit: contain" />
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import api from "@/api"
import moment from "moment"
import { mapState } from "vuex"
import userService from "@/services/userService"

export default {
  data() {
    return {
      userService,
      bills: [],
      studios: [],
      loading: false,
      loadingExport: false,
      uploading: false,
      showFilters: false,

      // View & Stats
      viewMode: "grid", // 'grid' | 'table'
      statsDialog: false,

      expanded: [],

      // Delete Logic
      deleteDialog: false,
      billToDelete: null,
      deleting: false,

      // Edit Mode
      isEditMode: false,
      editingId: null,

      // Dialogs
      dialog: false,
      previewDialog: false,
      aiStep: 1,
      uploadFile: null,
      currentUploadPreview: null,
      currentPreviewUrl: null,
      valid: false,
      dateMenuUpload: false,
      mobileTab: 0,

      // Table Headers
      tableHeaders: [
        { text: "Datum", value: "date", width: "100px" },
        { text: "Händler", value: "storeName" },
        { text: "Uploader", value: "uploader" },
        { text: "Studio", value: "studio", width: "120px" },
        { text: "Betrag", value: "amount", align: "right" },
        { text: "", value: "actions", sortable: false, align: "right", width: "110px" },
      ],

      // AI Loading State
      loadingMessage: "Starte Upload...",
      loadingInterval: null,
      loadingMessages: [
        "Lese Bilddaten...",
        "OCR Texterkennung läuft...",
        "Identifiziere Händler Logo...",
        "Suche nach Datumsangaben...",
        "Validiere Gesamtsumme...",
        "Fast fertig...",
      ],

      // Filters
      dateMenu: false,
      dateMode: 0,
      filters: {
        month: moment().format("YYYY-MM"),
        range: [],
        search: "",
        studioIds: [],
      },
      newBill: {
        storeName: "",
        date: moment().format("YYYY-MM-DD"),
        studio: null,
        amount: null,
        tax: null,
        lineItems: [],
      },
      mobileDateMenu: false,
    }
  },
  async created() {
    let studios = await api.fetchStudios()
    this.studios = studios.filter((s) => s.code && s.code !== "ot" && s.code !== "alibey").map((s) => ({ _id: s._id, name: s.name }))
    this.loadData()
  },
  computed: {
    canViewAll() {
      return userService.hasRight("cc_viewAll")
    },
    grandTotal() {
      return this.bills ? this.bills.reduce((sum, b) => sum + (b.amount || 0), 0) : 0
    },
    statsByStudio() {
      if (!this.bills) return {}
      const stats = {}
      this.bills.forEach((b) => {
        const name = b.studio ? b.studio.name : "Andere"
        stats[name] = (stats[name] || 0) + (b.amount || 0)
      })
      return stats
    },
    filterStudios() {
      return this.studios
    },
    dateDisplay() {
      if (this.dateMode === 0) return moment(this.filters.month).format("MMMM YYYY")
      if (this.filters.range.length === 2) {
        const d = [...this.filters.range].sort()
        return `${moment(d[0]).format("DD.MM")} - ${moment(d[1]).format("DD.MM.YY")}`
      }
      return "Zeitraum"
    },
  },
  methods: {
    // Hilfsfunktion: Base64 zu File Objekt
    dataURLtoFile(base64String, filename) {
      const arr = base64String.split(",")
      // Wenn der Header fehlt (kommt von Flutter oft pur), hinzufügen
      const b64 = arr.length > 1 ? arr[1] : arr[0]
      const mime = arr.length > 1 ? arr[0].match(/:(.*?);/)[1] : "image/jpeg"

      const bstr = atob(b64)
      let n = bstr.length
      const u8arr = new Uint8Array(n)
      while (n--) {
        u8arr[n] = bstr.charCodeAt(n)
      }
      return new File([u8arr], filename, { type: mime })
    },

    async triggerUploadFlow() {
      // CASE 1: Handy App -> Native Kamera nutzen
      if (this.$vuetify.breakpoint.xsOnly && window.flutter_inappwebview) {
        try {
          const result = await window.flutter_inappwebview.callHandler("openCameraNative")

          if (result && result.success) {
            // Wir basteln ein "Fake" File Objekt aus dem Base64
            // damit deine bestehende Logik unverändert bleibt
            const file = this.dataURLtoFile(result.base64, result.name || "camera_upload.jpg")
            this.onFileSelected(file)
          }
        } catch (e) {
          console.error("Native Camera Error", e)
          // Fallback, falls Native fehlschlägt
          this.$refs.fileInput.click()
        }
      }
      // CASE 2: Desktop / Webbrowser -> Normaler Upload
      else {
        this.$refs.fileInput.click()
      }
    },
    confirmDelete(bill) {
      this.billToDelete = bill
      this.deleteDialog = true
    },
    async executeDelete() {
      if (!this.billToDelete) return
      this.deleting = true
      try {
        await api.creditCardBills.delete(this.billToDelete._id)

        // Remove from list
        this.bills = this.bills.filter((b) => b._id !== this.billToDelete._id)

        this.$toast.success("Beleg gelöscht")
        this.deleteDialog = false
      } catch (e) {
        this.$toast.error("Fehler beim Löschen")
        console.error(e)
      } finally {
        this.deleting = false
        this.billToDelete = null
      }
    },
    formatDate(d) {
      return moment(d).format("DD.MM.YY")
    },
    getInitials(u) {
      return u ? (u.vorname[0] + u.nachname[0]).toUpperCase() : "?"
    },

    async loadData() {
      if (!this.uploading) this.loading = true

      try {
        const params = { search: this.filters.search, studioId: this.filters.studioIds }
        if (this.dateMode === 0) {
          const [y, m] = this.filters.month.split("-")
          params.month = m
          params.year = y
        } else if (this.filters.range.length === 2) {
          const d = [...this.filters.range].sort()
          params.dateFrom = d[0]
          params.dateTo = d[1]
        }
        const data = await api.creditCardBills.fetchAll(params)
        this.bills = data.map((b) => ({ ...b, previewUrl: null }))

        // NEU: Intelligentes Aufklappen
        if (this.filters.search && this.filters.search.length > 0) {
          const searchLower = this.filters.search.toLowerCase()

          // Klappe nur Belege auf, wo der Suchbegriff in den ITEMS steckt
          this.expanded = this.bills.filter((b) => b.lineItems && b.lineItems.some((item) => item.name.toLowerCase().includes(searchLower)))
        } else {
          // Keine Suche? Alles zuklappen (übersichtlicher)
          this.expanded = []
        }

        // Lazy Load Images
        this.bills.forEach(async (b) => {
          try {
            b.previewUrl = await api.creditCardBills.fetchImageBlob(b._id)
          } catch (e) {}
        })
      } catch (e) {
        console.error(e)
      } finally {
        this.loading = false
      }
    },
    onRangeInput() {
      if (this.filters.range.length === 2) this.loadData()
    },

    // --- UPLOAD FLOW ---
    openUploadDialog() {
      this.isEditMode = false
      this.editingId = null
      this.aiStep = 1
      this.uploadFile = null
      this.currentUploadPreview = null
      this.mobileTab = 0
      this.newBill = { storeName: "", date: moment().format("YYYY-MM-DD"), studio: null, amount: null, lineItems: [] }
      this.dialog = true
    },

    // --- EDIT FLOW ---
    openEditDialog(bill) {
      this.isEditMode = true
      this.editingId = bill._id
      this.aiStep = 3 // Direkt zum Formular
      this.mobileTab = 0
      this.uploadFile = null

      // Werte kopieren
      this.newBill = JSON.parse(
        JSON.stringify({
          storeName: bill.storeName,
          date: moment(bill.date).format("YYYY-MM-DD"),
          studio: bill.studio ? bill.studio._id : null,
          amount: bill.amount,
          tax: bill.tax,
          lineItems: bill.lineItems || [],
        }),
      )

      // Preview Bild vom bestehenden Bill nehmen
      this.currentUploadPreview = bill.previewUrl
      this.dialog = true
    },

    onFileSelected(file) {
      if (!file) return
      this.uploadFile = file
      this.currentUploadPreview = URL.createObjectURL(file)
      this.startAiAnalysis()
    },
    async startAiAnalysis() {
      if (!this.uploadFile) return
      this.aiStep = 2
      let msgIdx = 0
      this.loadingInterval = setInterval(() => {
        this.loadingMessage = this.loadingMessages[msgIdx % this.loadingMessages.length]
        msgIdx++
      }, 3500)

      const fd = new FormData()
      fd.append("file", this.uploadFile)

      try {
        const data = await api.creditCardBills.analyze(fd)
        if (data.storeName) this.newBill.storeName = data.storeName
        if (data.amount) this.newBill.amount = data.amount
        if (data.tax) this.newBill.tax = data.tax
        if (data.date) this.newBill.date = data.date
        if (data.lineItems) this.newBill.lineItems = data.lineItems

        setTimeout(() => {
          clearInterval(this.loadingInterval)
          this.aiStep = 3
        }, 500)
      } catch (e) {
        clearInterval(this.loadingInterval)
        this.$toast.error("Manuelle Eingabe erforderlich")
        this.aiStep = 3
      }
    },
    addLineItem() {
      this.newBill.lineItems.push({ name: "", price: null })
    },
    removeLineItem(i) {
      this.newBill.lineItems.splice(i, 1)
    },

    async saveBill() {
      if (!this.$refs.form.validate()) return
      this.uploading = true

      try {
        if (this.isEditMode) {
          // --- UPDATE CASE ---
          // Wir senden JSON, kein FormData (Bild ändert sich ja nicht)
          const payload = {
            storeName: this.newBill.storeName,
            date: this.newBill.date,
            studio: this.newBill.studio,
            amount: parseFloat(this.newBill.amount),
            tax: parseFloat(this.newBill.tax || 0),
            lineItems: this.newBill.lineItems,
          }

          const updatedBill = await api.creditCardBills.update(this.editingId, payload)

          // Lokales Update in Liste
          const idx = this.bills.findIndex((b) => b._id === this.editingId)
          if (idx !== -1) {
            // PreviewURL retten, da sie vom Server oft nicht neu kommt
            updatedBill.previewUrl = this.bills[idx].previewUrl
            this.$set(this.bills, idx, updatedBill)
          }
          this.$toast.success("Aktualisiert")
        } else {
          // --- UPLOAD CASE ---
          const fd = new FormData()
          fd.append("file", this.uploadFile)
          fd.append("date", this.newBill.date)
          fd.append("studio", this.newBill.studio)
          fd.append("amount", this.newBill.amount)
          fd.append("storeName", this.newBill.storeName)
          fd.append("lineItems", JSON.stringify(this.newBill.lineItems))
          fd.append("tax", this.newBill.tax || 0)

          const savedBill = await api.creditCardBills.upload(fd)
          savedBill.previewUrl = this.currentUploadPreview

          if (!savedBill.uploader) {
            savedBill.uploader = { vorname: "Ich", nachname: "" }
          }

          const billMonth = moment(savedBill.date).format("YYYY-MM")
          const currentFilterMonth = this.filters.month

          if (this.dateMode === 1 || billMonth === currentFilterMonth) {
            this.bills.unshift(savedBill)
          }
          this.$toast.success("Gespeichert")
        }

        this.dialog = false
        // Reload um sicher zu gehen (Studio Names etc populate)
        this.loadData()
      } catch (e) {
        this.$toast.error("Fehler: " + (e.response?.data || e.message))
        console.error(e)
      } finally {
        this.uploading = false
      }
    },

    async openPreview(bill) {
      if (!bill.previewUrl) bill.previewUrl = await api.creditCardBills.fetchImageBlob(bill._id)
      this.currentPreviewUrl = bill.previewUrl
      this.previewDialog = true
    },
    downloadFile(bill) {
      const link = document.createElement("a")
      link.href = bill.previewUrl
      link.download = "beleg.webp"
      link.click()
    },
    async downloadExport() {
      this.loadingExport = true
      try {
        let m, y
        if (this.dateMode === 0) {
          ;[y, m] = this.filters.month.split("-")
        } else {
          const d = moment()
          y = d.format("YYYY")
          m = d.format("MM")
        }
        await api.creditCardBills.downloadPdf(m, y)
      } catch (e) {
      } finally {
        this.loadingExport = false
      }
    },
  },
}
</script>

<style scoped>
.border-bottom-dashed {
  border-bottom: 1px dashed rgba(255, 255, 255, 0.1);
}
.border-bottom-dashed:last-child {
  border-bottom: none;
}
/* GENERAL UI */
.hide-scrollbar::-webkit-scrollbar {
  display: none;
}
.hide-scrollbar {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
.hover-card {
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
  border: 1px solid #333;
}
.hover-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.5) !important;
  border-color: #1976d2;
}
.border-card {
  border: 1px solid rgba(255, 255, 255, 0.05);
}

/* MOBILE ROWS */
.mobile-row {
  transition: background 0.2s;
  border-bottom: 1px solid #333;
}
.mobile-row:active {
  background: #333 !important;
}

/* UPLOAD ZONE */
.dashed-border {
  border: 2px dashed #444;
  transition: 0.3s;
}
.hover-scale:hover {
  border-color: #1976d2;
  background: #252525 !important;
}
.circle-bg {
  width: 80px;
  height: 80px;
  background: rgba(25, 118, 210, 0.1);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* SCANNER ANIMATION */
.scanner-container {
  position: relative;
  width: 150px;
  height: 150px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #333;
  border-radius: 12px;
  background: #111;
  overflow: hidden;
}
.scanner-line {
  position: absolute;
  width: 100%;
  height: 4px;
  background: #00e5ff;
  box-shadow: 0 0 15px #00e5ff;
  top: 0;
  animation: scan 2.5s ease-in-out infinite;
  z-index: 2;
}
@keyframes scan {
  0%,
  100% {
    top: 0%;
    opacity: 0;
  }
  10% {
    opacity: 1;
  }
  50% {
    top: 100%;
  }
  90% {
    opacity: 1;
  }
}

/* INPUT STYLES */
.custom-amount-input >>> input {
  font-size: 2.5rem !important;
  text-align: center;
  font-weight: 700;
  color: #4caf50 !important;
}
.border-highlight {
  border: 1px solid #333;
}
.shadow-glow {
  box-shadow: 0 0 15px rgba(25, 118, 210, 0.4);
}

@media (max-width: 600px) {
  .text-h4 {
    font-size: 1.5rem !important;
  }
}
</style>
